import { useState } from 'react';

export default function ServicesCTA() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (!formData.name || !formData.email || !formData.phone || !formData.service) {
      setSubmitStatus('error');
      return;
    }

    // Ensure message length does not exceed limit
    if (formData.message.length > 500) return;

    setIsSubmitting(true);
    try {
      const response = await fetch('https://readdy.ai/api/form/d6atsnfmvg9ih2c7ara0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as Record<string, string>).toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-24 bg-gradient-to-b from-gray-50 to-white relative z-10">
      <div className="max-w-7xl mx-auto px-8 lg:px-16">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left - Info */}
          <div className="pt-4">
            <p className="text-xs uppercase tracking-[0.3em] mb-4 font-semibold text-neon-green">
              Ready to Get Started?
            </p>
            <h2 className="text-4xl lg:text-5xl font-bold text-matte-black mb-6 leading-tight">
              Request Your <span className="text-neon-green">Free</span> Estimate Today
            </h2>
            <p className="text-base text-gunmetal leading-relaxed mb-10 max-w-lg">
              Whether you need a full roof replacement, storm damage repair, new siding, or gutter
              installation — our team is ready to help. Fill out the form and we&apos;ll respond
              within 24 hours.
            </p>

            <div className="space-y-6 mb-10">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-neon-green/10 rounded-lg">
                  <i className="ri-shield-check-line text-neon-green text-2xl"></i>
                </div>
                <div>
                  <h4 className="text-sm font-bold text-matte-black">Licensed &amp; Insured</h4>
                  <p className="text-xs text-gunmetal">Full coverage for your peace of mind</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-neon-green/10 rounded-lg">
                  <i className="ri-money-dollar-circle-line text-neon-green text-2xl"></i>
                </div>
                <div>
                  <h4 className="text-sm font-bold text-matte-black">Financing Available</h4>
                  <p className="text-xs text-gunmetal">Flexible payment options to fit your budget</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-neon-green/10 rounded-lg">
                  <i className="ri-customer-service-2-line text-neon-green text-2xl"></i>
                </div>
                <div>
                  <h4 className="text-sm font-bold text-matte-black">24/7 Emergency Service</h4>
                  <p className="text-xs text-gunmetal">Storm damage? We respond fast</p>
                </div>
              </div>
            </div>

            <a
              href="tel:717-280-2005"
              className="inline-flex items-center gap-3 bg-matte-black text-white px-8 py-4 rounded-lg font-semibold text-sm hover:bg-gunmetal transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-fill text-neon-green text-lg"></i>
              Call Now: 717-280-2005
            </a>
          </div>

          {/* Right - Form */}
          <div className="bg-matte-black rounded-2xl p-8 lg:p-10">
            {submitStatus === 'success' ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 flex items-center justify-center bg-neon-green/20 rounded-full mx-auto mb-4">
                  <i className="ri-check-line text-neon-green text-3xl"></i>
                </div>
                <h3 className="text-white text-2xl font-bold mb-2">Thank You!</h3>
                <p className="text-gray-400">We&apos;ll be in touch within 24 hours.</p>
              </div>
            ) : (
              <form
                id="services-quote-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <h3 className="text-white text-xl font-bold mb-2">Get Your Free Quote</h3>
                <p className="text-gray-400 text-sm mb-6">
                  Fill out the details below and we&apos;ll get back to you fast.
                </p>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-gray-300 text-sm mb-2">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full bg-gunmetal/60 border border-gray-700 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-neon-green transition-colors"
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-300 text-sm mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full bg-gunmetal/60 border border-gray-700 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-neon-green transition-colors"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-gray-300 text-sm mb-2">Phone *</label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full bg-gunmetal/60 border border-gray-700 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-neon-green transition-colors"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-300 text-sm mb-2">Service Needed *</label>
                    <select
                      name="service"
                      required
                      value={formData.service}
                      onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                      className="w-full bg-gunmetal/60 border border-gray-700 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-neon-green transition-colors cursor-pointer"
                    >
                      <option value="">Select a service</option>
                      <option value="Roof Replacement">Roof Replacement</option>
                      <option value="Roof Repair">Roof Repair</option>
                      <option value="Siding Installation">Siding Installation</option>
                      <option value="Gutter Installation">Gutter Installation</option>
                      <option value="Storm Restoration">Storm Restoration</option>
                      <option value="Free Inspection">Free Inspection</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-gray-300 text-sm mb-2">Project Details</label>
                  <textarea
                    name="message"
                    rows={4}
                    maxLength={500}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full bg-gunmetal/60 border border-gray-700 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-neon-green transition-colors resize-none"
                    placeholder="Tell us about your project..."
                  />
                  <p className="text-xs text-gray-500 mt-1 text-right">
                    {formData.message.length}/500
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-neon-green text-matte-black font-bold py-4 rounded-lg hover:bg-white transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 text-sm"
                >
                  {isSubmitting ? 'Sending...' : 'Get My Free Estimate'}
                </button>

                {submitStatus === 'error' && (
                  <p className="text-red-400 text-sm text-center">
                    Something went wrong. Please try again.
                  </p>
                )}
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
